import {createWebHistory, createRouter} from "vue-router";
import Home from "@/views/HomePage.vue";
import CreateAccount from "@/views/LogIn/CreateAccount.vue";
import FAQ from "@/views/FAQ.vue";
//import FindInstructors from "@/views/FindInstructors/FindInstructors.vue";
//import Instructor from "@/views/FindInstructors/Instructor.vue";
import Program from "@/views/Program.vue";
import Resources from "@/views/Resources.vue";
import AboutUs from "@/views/AboutUs.vue";
import ContactUs from "@/views/ContactUs.vue";
import StudentPage from '@/views/MyPage.vue';
import {auth} from '@/firebase.config'
import {getUser} from "@/api";
import FindStudents from "@/views/FindStudents/FindStudents.vue";
import Student from "@/views/FindStudents/Student.vue";
import NonDiscriminatoryPolicy from "@/views/NonDiscriminatoryPolicy.vue";
import SupportUs from "@/views/SupportUs.vue";
import AdminLayout from "@/views/admin/AdminLayout.vue";
import {getIsAdmin} from "@/api/admin";
import {updateSeo} from '@/utils/seo';

const routes = [
    { path: '/sso', name: 'SharedSignIn', component: () => import('@/views/SharedSignIn.vue') },
    {
        path: "/",
        name: "Home",
        component: Home,
        props: true,
        meta: {closing: true}
    },
    {
        path: '/student/:id',
        name: "Student",
        component: Student,
        props: true,
        meta: {
            requiresAuth: true
        }
    },
    {
        path: "/create-account",
        name: "CreateAccount",
        component: CreateAccount,
        props: true
    },
    {
        path: "/profile",
        name: "StudentPage",
        component: StudentPage,
        props: true,
        meta: {
            requiresAuth: true,
            requiresProfile: true
        }
    },
    {
        // The Season 12 preferences step is disabled; preferences are edited on
        // My Page. Kept as a named redirect so old links and pushes still land.
        path: "/preferences",
        name: "Preferences",
        redirect: {name: "StudentPage"},
    },
    {
        path: "/dreamers-blessing",
        name: "DreamersBlessing",
        component: () => import("@/views/DreamersBlessing.vue"),
    },
    {
        path: "/faq",
        name: "FAQ",
        component: FAQ,
        meta: {closing: true}
    },
    {
        path: "/find-students",
        name: "FindStudents",
        component: FindStudents,
        props: true,
        meta: {
            requiresAuth: true
        }
    },
    {
        path: "/programs",
        name: "Program",
        component: Program,
        props: true,
        meta: {closing: true}
    },
    {path: "/program", redirect: to => ({path: "/programs", hash: to.hash})},
    {
        path: "/resources",
        name: "Resources",
        component: Resources,
        props: true
    },
    {
        path: "/about",
        name: "AboutUs",
        component: AboutUs,
        props: true,
        meta: {closing: true}
    },
    {path: "/about-us", redirect: to => ({path: "/about", hash: to.hash})},
    {
        path: "/contact",
        name: "ContactUs",
        component: ContactUs,
        props: true
    },
    {
        path: "/policy",
        name: "NonDiscriminatoryPolicy",
        component: NonDiscriminatoryPolicy,
        props: true,
        meta: {closing: true}
    },
    {path: "/nondiscriminatory-policy", redirect: to => ({path: "/policy", hash: to.hash})},
    {
        path: "/support",
        name: "Support Us",
        component: SupportUs,
        props: true,
        meta: {closing: true}
    },
    {path: "/supportus", redirect: to => ({path: "/support", hash: to.hash})},
    {
        path: "/admin",
        component: AdminLayout,
        meta: {
            requiresAuth: true,
            requiresAdmin: true
        },
        children: [
            {
                path: "",
                name: "AdminDashboard",
                component: () => import("@/views/admin/AdminDashboard.vue"),
            },
            {
                path: "teachers",
                name: "AdminTeachers",
                component: () => import("@/views/admin/Teachers/TeacherList.vue"),
            },
            {
                path: "all-teachers",
                name: "AdminAllTeachers",
                component: () => import("@/views/admin/Teachers/AllTeachersList.vue"),
            },
            {
                path: "teachers/:uid",
                name: "AdminTeacherDetail",
                component: () => import("@/views/admin/Teachers/TeacherDetail.vue"),
                props: true,
            },
            {
                path: "students",
                name: "AdminStudents",
                component: () => import("@/views/admin/Students/StudentList.vue"),
            },
            {
                path: "all-students",
                name: "AdminAllStudents",
                component: () => import("@/views/admin/Students/AllStudentsList.vue"),
            },
            {
                path: "students/:uid",
                name: "AdminStudentDetail",
                component: () => import("@/views/admin/Students/StudentDetail.vue"),
                props: true,
            },
            {
                path: "classes",
                name: "AdminClasses",
                component: () => import("@/views/admin/Classes/ClassList.vue"),
            },
            {
                path: "classes/:cid",
                name: "AdminClassDetail",
                component: () => import("@/views/admin/Classes/ClassDetail.vue"),
                props: true,
            },
            {
                path: "reports",
                name: "AdminReports",
                component: () => import("@/views/admin/Reports/ReportsOverview.vue"),
            },
            {
                path: "video-links",
                name: "AdminVideoLinks",
                component: () => import("@/views/admin/VideoLinks/VideoLinkList.vue"),
            },
            {
                path: "reminders",
                name: "AdminReminders",
                component: () => import("@/views/admin/Reminders/ReminderPreview.vue"),
            },
            {
                path: "matching",
                name: "AdminMatchingStub",
                component: () => import("@/views/admin/Matching/MatchingStub.vue"),
            },
            {
                path: "dreamers-blessing",
                name: "AdminDreamersBlessing",
                component: () => import("@/views/admin/DreamersBlessing/ApplicationList.vue"),
            },
            {
                path: "settings/admin-accounts",
                name: "AdminAccounts",
                component: () => import("@/views/admin/Settings/AdminAccounts.vue"),
            },
        ],
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
    // Mirrors the design's router: section anchors (/support#give) scroll into
    // view below the sticky header, everything else starts at the top.
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition;
        if (to.hash) return {el: to.hash, top: 76};
        return {top: 0};
    },
});

router.beforeEach(async (to, from) => {
    let currentUser = auth.currentUser;
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);
    const requiresProfile = to.matched.some(record => record.meta.requiresProfile);
    const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin);
    //console.log("...currentUser", currentUser)

    if (requiresAuth) {
        if (currentUser) {
            if (!currentUser.emailVerified) {
                try {
                    await currentUser.reload();
                    currentUser = auth.currentUser;
                    localStorage.setItem('authUser', JSON.stringify(currentUser));
                } catch (error) {
                    console.error(error);
                }
            }

            if (requiresProfile) {
                try {
                    await getUser();
                } catch (error) {
                    if (error?.response?.status === 404) {
                        // Authenticated (e.g. via Google) but no `users` DB
                        // row exists at all yet — My Page can't render
                        // without one. Send them back to Home; signing in
                        // with Google again
                        // resumes them straight into the signup-completion
                        // flow (see loginOverlay.vue's onGoogleSignIn).
                        alert('Please finish creating your account — click Login and continue with Google to pick up where you left off.');
                        return {name: 'Home'};
                    }
                    console.error(error);
                }
            }

            if (requiresAdmin) {
                try {
                    const info = await getIsAdmin();
                    localStorage.setItem('adminAccess', JSON.stringify({
                        uid: currentUser.uid,
                        role: info?.role || '',
                    }));
                } catch (error) {
                    localStorage.removeItem('adminAccess');
                    console.error(error);
                    alert('Admin access required.');
                    return {name: 'Home'};
                }
            }

            if (currentUser.emailVerified) {
                if (from.path === '/create-account') {
                    return {name: 'Home'}
                }
                return true;
            } else {
                if (to.path === '/find-students') {
                    alert('Please verify your email to access this page.')
                    return {name: 'Home'} // bypass temporarily for testing
                }
                return true;
            }
        } else {
            alert('Please log in to access this page.')

            return {name: 'Home'}
        }
    } else {
        return true;
    }

});
router.afterEach((to, from, failure) => {
    if (!failure) updateSeo(to);
});

export default router;
